import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { STATUS_META } from "@/lib/constants";
import { useExecutives } from "@/context/ExecutivesContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Pencil, Trash2, Users, Inbox, CalendarClock, MessageSquareText } from "lucide-react";
import ManagerRemarksDialog from "@/components/ManagerRemarksDialog";

const TODAY = new Date().toISOString().slice(0, 10);

function FollowUpCell({ date, status }) {
  if (!date) return <span className="text-slate-300">—</span>;
  let cls = "text-slate-600";
  let label = date;
  if (status === "pending" && date < TODAY) {
    cls = "text-rose-600 font-medium";
    label = `${date} · overdue`;
  } else if (status === "pending" && date === TODAY) {
    cls = "text-amber-600 font-medium";
    label = `${date} · today`;
  }
  return (
    <span className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${cls}`}>
      <CalendarClock className="h-3 w-3" />
      {label}
    </span>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

export default function LeadTable({ leads, onChanged, onEdit }) {
  const { user } = useAuth();
  const { executives, execMap } = useExecutives();
  const canReassign = user.role === "sales_manager" || user.role === "management";
  const canDelete = user.role === "management";
  const canChangeExec = user.role === "management";
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [remarksLead, setRemarksLead] = useState(null);

  const updateStatus = async (lead, status) => {
    try {
      const { data } = await api.put(`/leads/${lead.id}`, { status });
      toast.success(`Status set to ${STATUS_META[status].label}`);
      onChanged(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reassign = async (lead, username) => {
    try {
      const { data } = await api.put(`/leads/${lead.id}`, { assigned_executive: username });
      toast.success(`Reassigned to ${execMap[username] || username}`);
      onChanged(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    const lead = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted by Management");
      onChanged(null, lead.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="leads-empty">
        <Inbox className="h-10 w-10 mb-3" />
        <p className="text-sm">No leads found for the current filters.</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Customer</TableHead>
              <TableHead>Event Date</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-center">Pax</TableHead>
              <TableHead>Source</TableHead>
              {canReassign && <TableHead>Executive</TableHead>}
              <TableHead>Follow-up</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => (
              <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className="hover:bg-slate-50/60">
                <TableCell className="font-medium text-slate-900">
                  {lead.customer_name}
                  {lead.remarks && (
                    <p className="text-xs text-slate-400 font-normal truncate max-w-[200px]">{lead.remarks}</p>
                  )}
                </TableCell>
                <TableCell className="text-slate-600 whitespace-nowrap">{lead.event_date}</TableCell>
                <TableCell className="text-slate-600 font-mono text-xs whitespace-nowrap">{lead.contact_no}</TableCell>
                <TableCell className="text-center font-mono">{lead.pax_count}</TableCell>
                <TableCell className="text-slate-600 whitespace-nowrap">{lead.source}</TableCell>
                {canReassign && !canChangeExec && (
                  <TableCell className="text-slate-600 text-sm whitespace-nowrap" data-testid={`executive-cell-${lead.id}`}>
                    {execMap[lead.assigned_executive] || lead.assigned_executive}
                  </TableCell>
                )}
                {canChangeExec && (
                  <TableCell>
                    <Select value={lead.assigned_executive} onValueChange={(v) => reassign(lead, v)}>
                      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`reassign-select-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {executives.map((ex) => (
                          <SelectItem key={ex.username} value={ex.username}>
                            {ex.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                )}
                <TableCell data-testid={`followup-cell-${lead.id}`}>
                  <FollowUpCell date={lead.follow_up_date} status={lead.status} />
                </TableCell>
                <TableCell>
                  <Select value={lead.status} onValueChange={(v) => updateStatus(lead, v)}>
                    <SelectTrigger className="h-8 w-[130px] border-0 p-0 shadow-none focus:ring-0" data-testid={`status-select-${lead.id}`}>
                      <StatusBadge status={lead.status} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS_META).map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_META[s].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-slate-500 text-xs whitespace-nowrap font-mono" data-testid={`created-cell-${lead.id}`}>
                  {lead.created_at ? lead.created_at.slice(0, 10) : "—"}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {canReassign && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Sales Manager remarks"
                      className="h-8 w-8 text-slate-500 hover:text-[#1E3A8A]"
                      data-testid={`manager-remarks-${lead.id}`}
                      onClick={() => setRemarksLead(lead)}
                    >
                      <MessageSquareText className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-500 hover:text-[#1E3A8A]"
                    data-testid={`edit-lead-${lead.id}`}
                    onClick={() => onEdit(lead)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                      data-testid={`delete-lead-button-${lead.id}`}
                      onClick={() => setDeleteTarget(lead)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {canReassign && <ManagerRemarksDialog lead={remarksLead} onOpenChange={(o) => !o && setRemarksLead(null)} />}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the lead for{" "}
              <span className="font-semibold">{deleteTarget?.customer_name}</span>. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete Lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { StatusBadge };
